import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Mail, MapPin, Users, Home, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatAddress } from './ContactFieldEditors';

function DonorPicker({ api, excludeIds, onSelect, placeholder }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/donors', { params: { q: q.trim() } });
        setResults(data);
      } catch { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, api]);
  const visible = results.filter(d => !excludeIds.includes(d.id)).slice(0, 8);
  return (
    <div className="relative flex-1 min-w-[180px]">
      <Input value={q} onChange={e => setQ(e.target.value)}
             placeholder={placeholder || 'Search donors…'} data-testid="donor-picker-input" />
      {visible.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded shadow max-h-48 overflow-auto">
          {visible.map(d => (
            <button key={d.id} type="button"
                    className="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                    onClick={() => { onSelect(d); setQ(''); setResults([]); }}
                    data-testid={`donor-picker-result-${d.id}`}>
              {d.name}{d.email ? ` · ${d.email}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-500 mb-2">
      <Icon className="h-3.5 w-3.5" /> {children}
    </div>
  );
}

export function ContactTab({ api, donor, onChanged }) {
  const navigate = useNavigate();
  const [pendingConn, setPendingConn] = useState(null);
  const [rel, setRel] = useState('');
  const [household, setHousehold] = useState(null);
  const [hhName, setHhName] = useState('');
  const [busy, setBusy] = useState(false);

  const loadHousehold = useCallback(async () => {
    if (!donor.household_id) { setHousehold(null); return; }
    try {
      const { data } = await api.get(`/donor-households/${donor.household_id}`);
      setHousehold(data);
    } catch { setHousehold(null); }
  }, [api, donor.household_id]);
  useEffect(() => { loadHousehold(); }, [loadHousehold]);

  const addConnection = async () => {
    if (!pendingConn) return;
    setBusy(true);
    try {
      await api.post(`/donors/${donor.id}/family-connections`, { donor_id: pendingConn.id, relationship: rel.trim() });
      toast.success('Connection added');
      setPendingConn(null); setRel('');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add connection'); }
    setBusy(false);
  };

  const removeConnection = async (otherId) => {
    try { await api.delete(`/donors/${donor.id}/family-connections/${otherId}`); toast.success('Connection removed'); onChanged(); }
    catch { toast.error('Failed to remove'); }
  };

  const createHousehold = async () => {
    const name = hhName.trim() || `The ${donor.last_name || donor.name} Family`;
    setBusy(true);
    try {
      await api.post('/donor-households', { name, member_ids: [donor.id] });
      toast.success(`Household "${name}" created`);
      setHhName('');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create household'); }
    setBusy(false);
  };

  const addHhMember = async (d) => {
    try {
      await api.put(`/donor-households/${donor.household_id}`, { add_member_ids: [d.id] });
      toast.success(`${d.name} added to household`);
      loadHousehold();
    } catch { toast.error('Failed to add member'); }
  };

  const removeHhMember = async (id) => {
    try {
      await api.put(`/donor-households/${donor.household_id}`, { remove_member_ids: [id] });
      toast.success('Removed from household');
      if (id === donor.id) onChanged(); else loadHousehold();
    } catch { toast.error('Failed to remove member'); }
  };

  const dissolveHousehold = async () => {
    if (!window.confirm('Dissolve this household? Members keep their records — only the grouping is removed.')) return;
    try { await api.delete(`/donor-households/${donor.household_id}`); toast.success('Household dissolved'); onChanged(); }
    catch { toast.error('Failed to dissolve'); }
  };

  const connections = donor.family_connections || [];
  const emails = donor.emails || [];
  const addresses = donor.addresses || [];

  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="contact-tab">
      {/* Emails */}
      <Card data-testid="contact-emails-card">
        <CardContent className="p-4">
          <SectionTitle icon={Mail}>Emails</SectionTitle>
          {emails.length === 0 ? <p className="text-sm text-gray-500">No emails on file.</p> : (
            <div className="space-y-1.5">
              {emails.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`contact-email-${i}`}>
                  <span className="flex-1 truncate">{e.email}</span>
                  {e.label && <Badge variant="outline" className="text-[10px]">{e.label}</Badge>}
                  {e.primary && <Badge className="bg-[#6B3FA0]/10 text-[#6B3FA0] border-0 text-[10px]">Primary</Badge>}
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-3">Add or edit via the "Edit donor" button above.</p>
        </CardContent>
      </Card>

      {/* Addresses */}
      <Card data-testid="contact-addresses-card">
        <CardContent className="p-4">
          <SectionTitle icon={MapPin}>Addresses</SectionTitle>
          {addresses.length === 0 ? <p className="text-sm text-gray-500">No addresses on file.</p> : (
            <div className="space-y-2">
              {addresses.map((a, i) => (
                <div key={i} className="text-sm" data-testid={`contact-address-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex-1">{formatAddress(a) || '—'}</span>
                    {a.label && <Badge variant="outline" className="text-[10px]">{a.label}</Badge>}
                    {a.primary && <Badge className="bg-[#6B3FA0]/10 text-[#6B3FA0] border-0 text-[10px]">Primary</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-3">Add or edit via the "Edit donor" button above.</p>
        </CardContent>
      </Card>

      {/* Family connections */}
      <Card data-testid="contact-connections-card">
        <CardContent className="p-4">
          <SectionTitle icon={Users}>Family connections</SectionTitle>
          {connections.length === 0 ? <p className="text-sm text-gray-500 mb-2">No linked family members yet.</p> : (
            <div className="space-y-1.5 mb-3">
              {connections.map(c => (
                <div key={c.donor_id} className="flex items-center gap-2 text-sm" data-testid={`connection-row-${c.donor_id}`}>
                  <button type="button" onClick={() => navigate(`/donors/${c.donor_id}`)}
                          className="text-[#6B3FA0] hover:underline font-medium"
                          data-testid={`connection-link-${c.donor_id}`}>
                    {c.donor_name}
                  </button>
                  {c.relationship && <span className="text-xs text-gray-500 capitalize">({c.relationship})</span>}
                  <div className="flex-1" />
                  <button type="button" onClick={() => removeConnection(c.donor_id)}
                          className="text-gray-400 hover:text-red-500" title="Remove connection"
                          data-testid={`connection-remove-${c.donor_id}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {pendingConn ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-[#6B3FA0]/10 text-[#6B3FA0] border-0">{pendingConn.name}</Badge>
              <Input value={rel} onChange={e => setRel(e.target.value)} placeholder="Relationship (spouse, parent…)"
                     className="flex-1 min-w-[140px]" data-testid="connection-relationship-input" />
              <Button size="sm" onClick={addConnection} disabled={busy} data-testid="connection-add-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Link'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setPendingConn(null); setRel(''); }}>Cancel</Button>
            </div>
          ) : (
            <div className="flex gap-1.5">
              <DonorPicker api={api} placeholder="Search a donor to link…"
                           excludeIds={[donor.id, ...connections.map(c => c.donor_id)]}
                           onSelect={setPendingConn} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Household */}
      <Card data-testid="contact-household-card">
        <CardContent className="p-4">
          <SectionTitle icon={Home}>Household</SectionTitle>
          {donor.household_id && household ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm" data-testid="household-name">{household.name}</span>
                <Badge variant="outline" className="text-[10px]">{household.members.length} member{household.members.length === 1 ? '' : 's'}</Badge>
                <div className="flex-1" />
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 text-xs"
                        onClick={dissolveHousehold} data-testid="household-dissolve-btn">Dissolve</Button>
              </div>
              <div className="space-y-1">
                {household.members.map(m => (
                  <div key={m.id} className="flex items-center gap-2 text-sm" data-testid={`household-member-${m.id}`}>
                    {m.id === donor.id ? <span className="font-medium">{m.name} (this donor)</span> : (
                      <button type="button" onClick={() => navigate(`/donors/${m.id}`)}
                              className="text-[#6B3FA0] hover:underline">{m.name}</button>
                    )}
                    <div className="flex-1" />
                    <button type="button" onClick={() => removeHhMember(m.id)}
                            className="text-gray-400 hover:text-red-500" title="Remove from household"
                            data-testid={`household-remove-${m.id}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <DonorPicker api={api} placeholder="Add a member…"
                           excludeIds={household.members.map(m => m.id)}
                           onSelect={addHhMember} />
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Group this donor with family for joint mailings.</p>
              <div className="flex gap-1.5">
                <Input value={hhName} onChange={e => setHhName(e.target.value)}
                       placeholder={`The ${donor.last_name || donor.name} Family`}
                       data-testid="household-name-input" />
                <Button size="sm" onClick={createHousehold} disabled={busy} data-testid="household-create-btn">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Create'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
